import { useState, useEffect } from 'react';
import { fetchCandidatesForJob, updateCandidateStage, addCandidateToJob } from '@/components/jobs/kanban/utils/candidateOperations';
import { KANBAN_STAGES, KanbanCandidate } from '@/components/jobs/kanban/types/index';
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { triggerHiredConfetti } from "@/utils/confetti";

export const useJobKanban = (jobId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<KanbanCandidate[]>([]);
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);
  
  // Group candidates by their stage
  const candidatesByStage = KANBAN_STAGES.reduce((acc, stage) => {
    acc[stage.id] = candidates.filter(c => c.stage === stage.id);
    return acc;
  }, {} as Record<string, KanbanCandidate[]>);
  
  // Initial fetch of candidates
  const fetchCandidates = async () => {
    if (!jobId) return;
    
    setLoading(true);
    const data = await fetchCandidatesForJob(jobId);
    setCandidates(data);
    setLoading(false);
  };
  
  useEffect(() => {
    if (jobId) {
      fetchCandidates();
    }
  }, [jobId]);

  // Poll for updates if there are candidates with score 0 (pending analysis)
  useEffect(() => {
    if (!jobId || candidates.length === 0) return;

    // Check if any candidate has a score of 0, which implies analysis is in progress or pending
    // Note: fetchCandidatesForJob doesn't currently return analysisInProgress, so we rely on score === 0
    const hasPendingAnalysis = candidates.some(c => c.score === 0 || c.analysisInProgress);

    if (hasPendingAnalysis) {
      const intervalId = setInterval(async () => {
        // Fetch candidates silently
        const data = await fetchCandidatesForJob(jobId);
        
        // Check if we have any updates that should trigger a re-render
        const shouldUpdate = data.some(newCandidate => {
          const currentCandidate = candidates.find(c => c.id === newCandidate.id);
          if (!currentCandidate) return true; // New candidate found
          
          // Check if score has changed (e.g. from 0 to something else)
          if (currentCandidate.score !== newCandidate.score) return true;
          
          return false;
        });

        if (shouldUpdate) {
          setCandidates(data);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(intervalId);
    }
  }, [jobId, candidates]);
  
  // Handle stage change when a candidate is dragged to a new column
  const handleStageChange = async (candidateId: string, newStage: string) => {
    // Find the match to update
    const matchToUpdate = candidates.find(c => c.id === candidateId);

    if (!matchToUpdate || !jobId) return false;

    const oldStage = matchToUpdate.stage;

    // Only proceed if there's an actual change
    if (oldStage === newStage) return true;

    // Update in the database
    const success = await updateCandidateStage(matchToUpdate.id, newStage);

    if (success) {
      // Update local state
      setCandidates(candidates.map(c =>
        c.id === candidateId ? { ...c, stage: newStage } : c
      ));

      // Trigger confetti when a candidate is hired! 🎉
      if (newStage === 'hired') {
        triggerHiredConfetti();
      }
    }

    return success;
  };
  
  // Open the add candidate dialog
  const handleAddCandidate = () => {
    setIsAddCandidateOpen(true);
  };
  
  // Add a candidate to the job
  const handleAddCandidateConfirm = async (candidateId: string) => {
    setIsAddCandidateOpen(false);

    if (!candidateId || !jobId) return;

    const success = await addCandidateToJob(jobId, candidateId);

    if (success) {
      // Refresh candidates list
      fetchCandidates();
    }
  };
  
  // Add multiple candidates to the job
  const addMultipleCandidates = async (candidateIds: string[]) => {
    if (!jobId || candidateIds.length === 0) return;
    
    try {
      // First check if any of these candidates are already matched with the job
      const { data: existingMatches, error: checkError } = await supabase
        .from('matches')
        .select('candidate_id')
        .eq('job_id', jobId)
        .in('candidate_id', candidateIds);
      
      if (checkError) throw checkError;
      
      // Filter out candidates that already have matches
      const existingIds = new Set(existingMatches?.map(match => match.candidate_id) || []);
      const newCandidateIds = candidateIds.filter(id => !existingIds.has(id));
      
      if (newCandidateIds.length === 0) {
        toast({
          title: "Info",
          description: "All selected candidates are already matched to this job."
        });
        return true;
      }
      
      // Create match entries for each new candidate
      const matchEntries = newCandidateIds.map(candidateId => ({
        job_id: jobId,
        candidate_id: candidateId,
        score: 0, // Default score
        status: 'matched' // Default status
      }));
      
      const { error } = await supabase.from('matches').insert(matchEntries);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${newCandidateIds.length} candidate(s) added to job`,
      });
      
      // Refresh candidates list
      fetchCandidates();
      
      return true;
    } catch (error: any) {
      console.error("Error adding multiple candidates:", error);
      toast({
        title: "Error",
        description: "Failed to add candidates to job",
        variant: "destructive",
      });
      return false;
    }
  };
  
  return {
    loading,
    candidates,
    candidatesByStage,
    kanbanStages: KANBAN_STAGES,
    isAddCandidateOpen,
    setIsAddCandidateOpen,
    handleStageChange,
    handleAddCandidate,
    handleAddCandidateConfirm,
    addMultipleCandidates,
    fetchCandidates,
    refreshCandidates: fetchCandidates
  };
};
